import { useRef, useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { Box, Container } from "@mui/material";

import { formConfig } from "./utils/formConfig";
import { formatPhoneNumber } from "@/utils/forms/formatPhoneNumber";
import { formSteps } from "./steps";
import { stepFields } from "./utils/stepFields";
import { postFleetInquiry } from "@/api/postFleetInquiry";

import Confirmation from "./steps/Confirmation";
import Notification from "../Notification/Notification";
import { FleetFormHeader, FormButtons } from "./components";
import { formStyles, wrapperStyles } from "./styles";
import Captcha, { CaptchaRef } from "../Captcha/Captcha";

const FleetInquiryForm = () => {
  const [activeStep, setActiveStep] = useState(1);
  const [captchaValid, setCaptchaValid] = useState<ICaptchaValid>({
    valid: false,
    value: "",
  });

  // Error submit form state
  const [notificationIsOpen, setNotificationIsOpen] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  // Form control using React Hook Form
  const methods = useForm<IFleetInquiryForm>(formConfig);
  const { handleSubmit, trigger, reset } = methods;

  // Checks whether current step fields are valid and go to the next step
  const handleNext = async () => {
    // Get fields to validate for the current step
    const fieldsToValidate = stepFields[activeStep - 1];

    // Trigger validation for fields in the current step
    let isValidStep = true;
    for (const field of fieldsToValidate) {
      isValidStep = isValidStep && (await trigger(field));
    }

    if (isValidStep) {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  // Handle back step
  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  // Handle submit form data
  const onSubmit: SubmitHandler<IFleetInquiryForm> = async (data) => {
    if (captchaValid.valid) {
      const {
        preferredMode,
        numberOfVehicles,
        timeTable,
        phoneNumber,
        ...restData
      } = data;

      const transformedData: IFleetInquiryData = {
        ...restData,
        phoneNumber: formatPhoneNumber(phoneNumber),
        preferredMode: preferredMode === 0 ? "email" : "phone",
        numberOfVehicles: numberOfVehicles.toString(),
        timeTable: `${timeTable} months`,
      };

      const response = await postFleetInquiry(transformedData, captchaValid);

      if (response?.status === 200) {
        setActiveStep((prevStep) => prevStep + 1);
        captchaRef.current?.resetCaptcha();
        reset();
      }

      if (response?.status === 400) {
        setNotificationIsOpen(true);
        setError(response.data);

        setTimeout(() => {
          setNotificationIsOpen(false);
        }, 2500);
      }
    }
  };

  const captchaRef = useRef<CaptchaRef>(null);
  const handleCaptchaVerify = (captcha: ICaptchaValid) => {
    setCaptchaValid(captcha);
  };

  return (
    <>
      <FleetFormHeader activeStep={activeStep} totalSteps={formSteps.length} />
      <Box
        component="main"
        pt={{
          xs: 5,
          md: activeStep > formSteps.length ? 8.5 : 12.5,
          lg: activeStep > formSteps.length ? 8.5 : 20,
        }}
        pb={{ xs: 3, md: 12.5, lg: 26.5 }}
      >
        <Container sx={wrapperStyles}>
          {activeStep <= formSteps.length && (
            <Box
              component="form"
              onSubmit={handleSubmit(onSubmit)}
              display="flex"
              flexDirection="column"
              maxWidth={662}
              mx="auto"
              sx={formStyles}
            >
              {/* Form steps */}
              {formSteps.map(({ StepComponent }, index: number) => (
                <Box
                  key={index}
                  display={activeStep === index + 1 ? "block" : "none"}
                  mb={{ xs: 5, md: 8, lg: 10 }}
                >
                  <StepComponent methods={methods} />
                </Box>
              ))}
              {activeStep === formSteps.length && (
                <Box mb={4}>
                  <Captcha onVerify={handleCaptchaVerify} ref={captchaRef} />
                </Box>
              )}

              <FormButtons
                handleNext={handleNext}
                handleBack={handleBack}
                activeStep={activeStep}
                totalSteps={formSteps.length}
                valid={captchaValid.valid}
              />
            </Box>
          )}

          {/* Confirmation step */}
          {activeStep > formSteps.length && <Confirmation />}
        </Container>
      </Box>

      <Notification
        open={notificationIsOpen}
        onClose={() => setNotificationIsOpen(false)}
        title="Error"
        description={error}
        severity="error"
      />
    </>
  );
};

export default FleetInquiryForm;
